import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, User, Loader2, FileText, Flame, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useSeo } from '../components/custom/Seo';
import axios from 'axios';
import { logError } from '../lib/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DCSoccer = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useSeo({
    title: 'DC Soccer News – DC United & Washington Spirit | DC Sports Report',
    description:
      'The latest DC United and Washington Spirit news, analysis and coverage from DC Sports Report, an independent Washington, D.C. sports publication.',
    path: '/dcsoccer',
  });

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    try {
      // Fetch articles for both teams
      const [unitedRes, spiritRes] = await Promise.all([
        axios.get(`${API}/articles/team/dcunited`),
        axios.get(`${API}/articles/team/spirit`)
      ]);
      
      const allArticles = [
        ...(unitedRes.data.articles || []),
        ...(spiritRes.data.articles || [])
      ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      
      setArticles(allArticles);
    } catch (error) {
      logError('Error fetching articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const isNew = (dateString) => {
    const articleDate = new Date(dateString);
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    return articleDate >= weekAgo;
  };

  const getTeamInfo = (team) => {
    if (team === 'dcunited') {
      return { name: 'DC United', color: '#000000' };
    } else if (team === 'spirit') {
      return { name: 'Spirit', color: '#C4122E' };
    }
    return { name: 'DC Soccer', color: '#000000' };
  };

  // If no articles, show coming soon page
  if (!loading && articles.length === 0) {
    return (
      <div 
        data-testid="dcsoccer-page" 
        className="min-h-screen flex flex-col"
        style={{ background: 'linear-gradient(135deg, #000000 0%, #000000 50%, #C4122E 50%, #C4122E 100%)' }}
      >
        <div className="flex-1 flex flex-col items-center justify-center px-4 text-center">
          <div className="absolute top-24 left-4 sm:left-8">
            <Link to="/">
              <Button
                variant="outline"
                data-testid="back-to-home-button"
                className="bg-black/20 hover:bg-black/40 text-white border-white/30 hover:border-white"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </div>

          {/* Team Badges */}
          <div className="flex items-center gap-4 mb-6">
            <div className="px-4 py-2 text-sm font-bold uppercase tracking-widest border-2 bg-black text-white border-white">
              DC United
            </div>
            <span className="text-white text-2xl font-bold">+</span>
            <div className="px-4 py-2 text-sm font-bold uppercase tracking-widest border-2 bg-[#C4122E] text-white border-white">
              Spirit
            </div>
          </div>

          <h1 className="font-heading text-5xl sm:text-6xl md:text-7xl lg:text-8xl uppercase tracking-tight leading-none mb-8 text-white drop-shadow-lg">
            DC Soccer
          </h1>

          <div className="px-8 py-4 border-4 shadow-brutal-lg animate-flash bg-white border-black">
            <p className="font-heading text-2xl sm:text-3xl md:text-4xl uppercase tracking-wide text-black">
              Full Coverage Coming Soon
            </p>
          </div>

          <p className="mt-8 text-lg sm:text-xl max-w-md opacity-90 text-white bg-black/30 p-4 backdrop-blur-sm">
            We're working on bringing you the best DC United and Washington Spirit coverage. 
            Check back soon for news, analysis, and more!
          </p>

          <Link to="/#newsletter" className="mt-8">
            <Button className="h-14 px-8 font-bold uppercase tracking-wide border-2 shadow-brutal hover:shadow-brutal-lg hover:-translate-y-1 transition-all bg-white text-black border-black hover:bg-[#C4122E] hover:text-white hover:border-white">
              Subscribe for Updates
            </Button>
          </Link>
        </div>

        {/* Bottom stripe with both team colors */}
        <div className="flex h-4">
          <div className="flex-1 bg-black"></div>
          <div className="flex-1 bg-[#EF3E42]"></div>
          <div className="flex-1 bg-[#C4122E]"></div>
          <div className="flex-1 bg-white"></div>
        </div>
      </div>
    );
  }

  const featuredArticle = articles[0];
  const otherArticles = articles.slice(1);

  // Show articles
  return (
    <div data-testid="dcsoccer-page" className="min-h-screen bg-slate-100">
      {/* Hero Header */}
      <div className="py-12 sm:py-16 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/">
            <Button
              variant="outline"
              data-testid="back-to-home-button"
              className="mb-6 bg-black/20 hover:bg-black/40 text-white border-white/30 hover:border-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
          
          {/* Team Badges */}
          <div className="flex items-center gap-3 mb-4">
            <div className="px-3 py-1 text-xs font-bold uppercase tracking-widest border-2 bg-black text-white border-white">
              DC United • MLS
            </div>
            <div className="px-3 py-1 text-xs font-bold uppercase tracking-widest border-2 bg-[#C4122E] text-white border-white">
              Spirit • NWSL
            </div>
          </div>
          
          <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl text-white uppercase tracking-tight leading-none">
            DC Soccer
          </h1>
        </div>
      </div>

      {/* Featured Article */}
      {featuredArticle && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8">
          <Link to={`/article/${featuredArticle.id}`}>
            <Card 
              className="border-4 border-black shadow-brutal-lg hover:-translate-y-2 transition-all cursor-pointer overflow-hidden"
              style={{ borderTopColor: getTeamInfo(featuredArticle.team).color, borderTopWidth: '8px' }}
            >
              <CardContent className="p-0">
                <div className="grid md:grid-cols-3">
                  <div 
                    className="p-8 flex flex-col justify-center"
                    style={{ backgroundColor: getTeamInfo(featuredArticle.team).color }}
                  >
                    <div className="flex items-center gap-2 mb-4">
                      {isNew(featuredArticle.created_at) && (
                        <span className="bg-[#FFB612] text-[#002868] px-3 py-1 text-xs font-bold uppercase tracking-wide animate-pulse flex items-center gap-1">
                          <Flame className="w-3 h-3" />
                          New
                        </span>
                      )}
                      <span className="text-white/70 text-sm font-bold uppercase">Featured</span>
                    </div>
                    <h2 className="font-heading text-2xl sm:text-3xl text-white uppercase tracking-tight leading-tight">
                      {featuredArticle.title}
                    </h2>
                    <div className="mt-4 flex items-center gap-4 text-white/70 text-sm">
                      <span className="flex items-center gap-1">
                        <User className="w-4 h-4" />
                        {featuredArticle.author}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {formatDate(featuredArticle.created_at)}
                      </span>
                    </div>
                  </div>
                  
                  <div className="md:col-span-2 p-8 bg-white flex flex-col justify-center">
                    <p className="text-slate-600 text-lg leading-relaxed line-clamp-3 mb-6">
                      {featuredArticle.summary || featuredArticle.content.substring(0, 250) + '...'}
                    </p>
                    <div className="flex items-center gap-2 text-[#002868] font-bold uppercase tracking-wide group">
                      Read Full Article 
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-2 transition-transform" />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>
      )}

      {/* Other Articles */}
      {otherArticles.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h2 className="font-heading text-2xl text-slate-900 uppercase tracking-tight mb-8 flex items-center gap-2">
            <FileText className="w-6 h-6 text-black" />
            More Articles
          </h2>

          <div className="grid md:grid-cols-2 gap-6">
            {otherArticles.map((article) => {
              const teamInfo = getTeamInfo(article.team);
              return (
                <Link key={article.id} to={`/article/${article.id}`}>
                  <Card className="h-full border-2 border-slate-200 hover:border-black transition-all hover:-translate-y-1 shadow-sm hover:shadow-brutal cursor-pointer group">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-2 mb-3">
                        {isNew(article.created_at) && (
                          <span className="bg-[#FFB612] text-[#002868] px-2 py-1 text-xs font-bold uppercase tracking-wide animate-pulse flex items-center gap-1">
                            <Flame className="w-3 h-3" />
                            New
                          </span>
                        )}
                        <span 
                          className="px-3 py-1 text-xs font-bold uppercase tracking-wide text-white"
                          style={{ backgroundColor: teamInfo.color }}
                        >
                          {teamInfo.name}
                        </span>
                      </div>
                      
                      <h3 className="font-heading text-xl text-slate-900 uppercase tracking-tight mb-3 group-hover:text-[#002868] transition-colors">
                        {article.title}
                      </h3>
                      
                      {article.summary && (
                        <p className="text-slate-600 mb-4 line-clamp-2">
                          {article.summary}
                        </p>
                      )}
                      
                      <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <User className="w-4 h-4" />
                          {article.author}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(article.created_at)}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {/* Bottom Accent */}
      <div className="h-4 bg-[#EF3E42]" />
    </div>
  );
};

export default DCSoccer;
